import React, { useContext, useState } from 'react';
import {
  Box,
  Paper,
  Radio,
  Stack,
  Divider,
  TextField,
  Typography,
  RadioGroup,
  CircularProgress,
  FormControlLabel,
} from '@mui/material';
import moment from 'moment';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import {
  Configuration,
  useAftermarketProducts,
  mapListingToConfiguration,
  mapProductConfigToListing,
  AftermarketProductConfigTabs,
  AftermarketProductCatalogForm,
  AftermarketProductConfigurator,
  getInitialConfigKeysFromCriteria,
  AftermarketProductManagementHelpers,
} from 'modules/aftermarket';
import LooksOneIcon from '@mui/icons-material/LooksOne';
import LooksTwoIcon from '@mui/icons-material/LooksTwo';
import Autocomplete from '@mui/material/Autocomplete';
import { useSelector } from 'react-redux';
import { useHistory, useParams } from 'react-router-dom';
import { BusinessRule } from '@trnsact/business-criteria';
import _ from 'lodash';
import { AftermarketProductCategory, AftermarketProductType } from '@trnsact/trnsact-shared-types';
import { AFTERMARKET_PRODUCT_EVENTS } from '@trnsact/business-criteria/dist/src/models/rule-event/aftermarket/aftermarket-product-events';
import { useQuery } from '@apollo/client';
import { portalConfigurationTypes } from 'pages/Prequal/constants';
import { FormInput } from 'components/form';
import { Nullable, SelectOption, TabsOption } from 'global';
import { PageContainer } from 'components/shared/Page/PageContainer';
import { RenderWithCondition } from 'components/shared/Conditional/RenderWithCondition';
import { RenderWithFallback } from 'components/shared/Conditional/RenderWithFallback';
import { aftermarketProductsApi } from 'modules/aftermarket/products/api';
import {
  EditRestrictionReason,
  ProductConfig,
  ProductPageDocumentsState,
} from 'modules/aftermarket/products/types/products';
import {
  AddonsOptionsResponse,
  AddonsOptionsVariables,
  AftermarketProductCatalogDetails,
  AftermarketProductDocumentsResponse,
  AftermarketProductDocumentsVariables,
  GetAftermarketProductByIdResponse,
  GetAftermarketProductByIdVariables,
  MutationDTO,
} from 'modules/aftermarket/products/api/types';
import { logError } from 'utils/logger';
import { convertEnumToSelectOptions } from 'utils/selectOptionsHelper';
import { PageHeader } from './PageHeader';
import { VendorSection } from './VendorSection';
import { TabsContainer } from 'components/shared/TabsContainer/TabsContainer';
import { LogoSection } from './LogoSection';
import { mapCriteriaToConditions } from 'modules/jsonRulesCriterias/businessCriteria';
import { removeGraphQLMEtaFields } from '../../utils/removeGraphQLMEtaFields';
import { DocumentsCard } from '../../modules/aftermarket/products/ui/documentsCard/DocumentsCard';
import { AftermarketLicenceType } from 'modules/desking/types';
import { EditRestrictionReasonMessage } from './EditRestrictionReasonMessage';
import { CommonDataContext } from 'contexts/CommonDataContext';

export const AftermarketProductPage = () => {
  const history = useHistory();
  const { aftermarketProductId } = useParams<{ aftermarketProductId: string | undefined }>();

  const { isSuperAdminOrSingleAccount } = useContext(CommonDataContext);

  const account = useSelector((state: any) => state.account);
  const userProfile = useSelector((state: any) => state.userProfile);
  const portalConfiguration = useSelector((state: any) => state.portalConfiguration);

  const { handleAftermarketProductUpdate, handleAftermarketProductCreate, isLoading } = useAftermarketProducts();

  const [configurations, setConfigurations] = useState<Configuration[]>([]);
  const [existingUniqueCriteriaFacts, setExistingUniqueCriteriaFacts] = useState<string[]>([]);
  const [mappedProductConfigForListing, setMappedProductConfigForListing] = useState<Nullable<ProductConfig[]>>(
    aftermarketProductId ? null : []
  );
  const [editRestrictionReason, setEditRestrictionReason] = useState<EditRestrictionReason>();

  const { loading: isPartnerLinksLoading } = useQuery(aftermarketProductsApi.getPartnerLinks, {
    variables: { accountId: account.id, withDealerMetaData: true },
    onCompleted({ aftermarketDealerPartnerLink }) {
      const dataReceived = aftermarketDealerPartnerLink ?? [];

      setAvailableVendors(
        dataReceived
          .toSorted((a: any, b: any) => a?.name?.toLowerCase()?.localeCompare(b?.name?.toLowerCase()))
          .map((pl: any) => ({
            value: pl.partnerLinkId,
            isActive: pl.dealerMetaData.isActive ?? true,
            label: AftermarketProductManagementHelpers.getPartnerLinkLabel(pl),
          }))
      );
    },
  });

  const { data: addonsOptions, loading: isAddonsOptionsLoading } = useQuery<
    AddonsOptionsResponse,
    AddonsOptionsVariables
  >(aftermarketProductsApi.getAddonsOptionsByAccountId, {
    variables: { accountId: account.id },
  });

  const { loading: isProductLoading } = useQuery<GetAftermarketProductByIdResponse, GetAftermarketProductByIdVariables>(
    aftermarketProductsApi.getProductsById,
    {
      skip: !aftermarketProductId,
      variables: {
        accountId: account.id,
        productId: aftermarketProductId!,
      },
      onCompleted({ aftermarketProductById: product }) {
        if (!product) return;

        const isProductCreatedByCurrentAccount = product.accountIdCreatedBy === account.id;
        const portalSettingsConfig =
          portalConfiguration?.[portalConfigurationTypes.aftermarket]?.jsonDefinition?.config;

        const canWriteByUserRole = isPartnerAccount
          ? true
          : portalSettingsConfig?.rolesSettings?.assignedOnly?.includes(userProfile.vendorContactRole);
        const canWriteByLicenseType = isPartnerAccount
          ? true
          : portalSettingsConfig?.aftermarketLicenceType === AftermarketLicenceType.Enterprise;

        setIsProductCreatedByCurrentAccount(isProductCreatedByCurrentAccount);
        setProductName(product.productName);
        setCanWrite(
          (isProductCreatedByCurrentAccount && canWriteByUserRole && canWriteByLicenseType) ||
            isSuperAdminOrSingleAccount
        );

        let editRestrictionReason: EditRestrictionReason | undefined;
        if (!isProductCreatedByCurrentAccount && !!aftermarketProductId) {
          editRestrictionReason = EditRestrictionReason.SHARED_PRODUCT;
        } else if (!canWriteByLicenseType) {
          editRestrictionReason = EditRestrictionReason.LICENSE_TYPE;
        } else if (!canWriteByUserRole) {
          editRestrictionReason = EditRestrictionReason.USER_ROLE;
        }
        setEditRestrictionReason(editRestrictionReason);

        if (product.aftermarketVendorApiChannel) {
          setProductManagedByApi(true);
          setForceTab(AftermarketProductConfigTabs.ProductCatalog);
        }

        if (product.externalId) {
          setExternalId(product.externalId);
        }

        setBaseOrOption(product.productType);

        setProductCatalog(
          removeGraphQLMEtaFields(product.aftermarketProductCatalogDetails as AftermarketProductCatalogDetails)
        );

        setProductLogoUrl(product.productLogoUrl);
        setFirstDateAvailable(product.firstDateAvailable ?? null);
        setLastDateAvailable(product.lastDateAvailable ?? null);

        const chosenVendors = product.partnerLinks.map<SelectOption>((pl: any) => {
          const v = availableVendors.find(v => v.value === pl.partnerLinkId);

          return {
            label: _.get(v, 'partnerProfile.name', 'Unknown Vendor'),
            value: pl.partnerLinkId,
          };
        });

        if (chosenVendors) {
          setSelectedVendor(chosenVendors);
        }

        setProductType(product.productCategory);

        const mappedConfig = mapProductConfigToListing(product);

        setExistingUniqueCriteriaFacts(getInitialConfigKeysFromCriteria(product?.criteria ?? []));
        setMappedProductConfigForListing(mappedConfig);
        setConfigurations(mappedConfig.map((listItem, index) => mapListingToConfiguration(listItem, index)));
      },
    }
  );

  useQuery<AftermarketProductDocumentsResponse, AftermarketProductDocumentsVariables>(
    aftermarketProductsApi.getDocumentsByAftermarketProductId,
    {
      skip: !aftermarketProductId,
      variables: { aftermarketProductId: aftermarketProductId! },
      onCompleted(data) {
        setDocuments({
          ...documents,
          initialDocuments: data.aftermarketProductDocuments,
        });
      },
    }
  );

  const [productLogo, setProductLogo] = useState<File | null>(null);
  const [productLogoPreview, setProductLogoPreview] = useState<string | null>(null);

  const [forceTab, setForceTab] = useState<AftermarketProductConfigTabs>(AftermarketProductConfigTabs.Base);

  const [canWrite, setCanWrite] = useState(true);

  const [selectedVendor, setSelectedVendor] = useState<SelectOption[]>([]);
  const [selectedOptions, setSelectedOptions] = useState<SelectOption[]>([]);
  const [availableVendors, setAvailableVendors] = useState<SelectOption[]>([]);
  const [productName, setProductName] = useState<string>('');
  const [externalId, setExternalId] = useState<string | null>(null);
  const [productType, setProductType] = useState<string>(AftermarketProductCategory.Gap);
  const [baseOrOption, setBaseOrOption] = useState<AftermarketProductType>(AftermarketProductType.Product);
  const [isProductCreatedByCurrentAccount, setIsProductCreatedByCurrentAccount] = useState<boolean>(false);
  const [productManagedByApi, setProductManagedByApi] = useState<boolean>(false);
  const [documents, setDocuments] = useState<ProductPageDocumentsState>({
    initialDocuments: [],
    documentsToAdd: [],
    documentsToRemove: [],
  });

  const isPartnerAccount = account.accountType === 'Partner';

  const [productCatalog, setProductCatalog] = useState<AftermarketProductCatalogDetails>({
    productSummary: '',
    legalDisclaimer: '',
    coverageDetails: '',
    paragraphDescription: '',
    limitationsOfLiability: '',
    eligibilityAndInclusions: '',
  });

  const [productLogoUrl, setProductLogoUrl] = useState<string | null>(null);
  const [firstDateAvailable, setFirstDateAvailable] = useState<string | null>(null);
  const [lastDateAvailable, setLastDateAvailable] = useState<string | null>(null);
  const [dateValidationError, setDateValidationError] = useState<boolean>(false);

  const validateDates = (firstDate: string | null, lastDate: string | null) => {
    if (firstDate && lastDate) {
      const first = moment(firstDate);
      const last = moment(lastDate);
      return last.isBefore(first);
    }
    return false;
  };

  const handleProductLogoChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = event.target.files?.[0] || null;

    if (selectedFile) {
      const reader = new FileReader();

      reader.onloadend = () => {
        setProductLogoPreview(reader.result?.toString() ?? null);
      };
      reader.readAsDataURL(selectedFile);
    } else {
      setProductLogoPreview(null);
    }

    setProductLogo(selectedFile);
  };

  const handleSaveAftermarketProduct = async () => {
    const rules = configurations.map(config => {
      const rule = new BusinessRule({
        name: config.name,
        eventParams: {
          type: AFTERMARKET_PRODUCT_EVENTS.AFTERMARKET_PRODUCT_QUALIFIED,
          params: {
            DEALER_COST: config.dealerCost,
            MARKUP: {
              AMOUNT: config.dealerMarkup,
              TYPE: config.dealerMarkupType,
            },
            RATE_FACTOR: config.rateFactor,
            PRICING_MAX: config.maxPricing,
            PRICING_MIN: config.minPricing,
            SUGGESTED_RETAIL_PRICE: config.suggestedRetailPricing,
          },
        },
      });

      const conditions = Object.values(config.configRows);
      const conditionsForSave = mapCriteriaToConditions(conditions);
      const conditionsForSaveWithRequiredForQuote = conditionsForSave.map(condition => ({
        ...condition,
        requiredForQuote: true,
      }));
      conditionsForSaveWithRequiredForQuote.forEach(condition => rule.Conditions.addCondition(condition));

      return rule;
    });

    try {
      const dto: MutationDTO = {
        criteria: rules.map(rule => rule.getRule()),
        productLogo,
        productDescriptionInternal: null,
        partnerLinkIds: selectedVendor.map(vendor => vendor.value),
        productCategory: productType as any, // Yes, productType is the category
        productName: productName?.trim(),
        productType: baseOrOption,
        addonIds: selectedOptions.map(option => option.value),
        aftermarketProductCatalogDetails: productCatalog,
        ...(externalId ? { externalId } : {}),
        ...(firstDateAvailable ? { firstDateAvailable } : {}),
        ...(lastDateAvailable ? { lastDateAvailable } : {}),
      };

      if (aftermarketProductId) {
        await handleAftermarketProductUpdate({ aftermarketProductId, ...dto }, documents);
      } else {
        await handleAftermarketProductCreate({ ...dto, sourceOfCreation: 'base' }, documents);
      }
    } catch (error) {
      logError(error);
    }
    history.push('/aftermarket');
  };

  const updateBaseOrOption = (type: AftermarketProductType) => {
    if (type === AftermarketProductType.Addon) setForceTab(AftermarketProductConfigTabs.Base);
    setBaseOrOption(type);
  };

  const tabs: TabsOption<AftermarketProductConfigTabs>[] = [
    {
      value: AftermarketProductConfigTabs.Base,
      label: `Base ${baseOrOption === AftermarketProductType.Product ? 'Product' : 'Option'}`,
      component: (
        <RenderWithFallback isLoading={!Array.isArray(mappedProductConfigForListing)}>
          <RenderWithCondition condition={!productManagedByApi} fallBack={<>Managed by API</>}>
            <Box sx={{ marginTop: '0.5rem' }}>
              <AftermarketProductConfigurator
                initialConfigKeys={existingUniqueCriteriaFacts}
                initialConfigurationList={mappedProductConfigForListing!}
                isReadOnly={!canWrite}
                onSubscribe={listOfConfiguration => {
                  setConfigurations(
                    listOfConfiguration.map((listItem, index) => mapListingToConfiguration(listItem, index))
                  );
                }}
              />
            </Box>
          </RenderWithCondition>
        </RenderWithFallback>
      ),
    },
    {
      value: AftermarketProductConfigTabs.Addons,
      label: 'Addons',
      disabled: baseOrOption === AftermarketProductType.Addon,
      component: (
        <RenderWithCondition condition={!productManagedByApi} fallBack={<div>Managed by API</div>}>
          <Stack gap="1rem" marginTop="0.5rem">
            <Typography variant="subtitle1" color="primary">
              <LooksOneIcon /> Select Options on this Product
            </Typography>

            <Autocomplete
              multiple
              size="small"
              value={selectedOptions}
              loading={isAddonsOptionsLoading}
              options={addonsOptions?.options ?? []}
              disabled={!canWrite}
              onChange={(event, newValues) => {
                setSelectedOptions(newValues);
              }}
              renderInput={params => (
                <TextField {...params} label="Select Options on this Product" variant="outlined" />
              )}
            />
          </Stack>
        </RenderWithCondition>
      ),
    },
    {
      value: AftermarketProductConfigTabs.ProductCatalog,
      label: 'Product Catalog',
      component: (
        <Box marginTop="0.5rem">
          <AftermarketProductCatalogForm
            canWrite={canWrite}
            value={productCatalog}
            onChange={(key, value) => {
              setProductCatalog(prev => ({ ...prev, [key]: value }));
            }}
          />
        </Box>
      ),
    },
  ];

  return (
    <PageContainer>
      <RenderWithFallback
        isLoading={isProductLoading}
        fallBack={
          <Stack alignItems="center" justifyContent="center" width="100%" height="60rem">
            <CircularProgress />
          </Stack>
        }
      >
        <Stack gap="1rem">
          <PageHeader
            canWrite={canWrite}
            onBack={history.goBack}
            isLoading={isLoading}
            aftermarketProductId={aftermarketProductId ?? null}
            handleSaveAftermarketProduct={handleSaveAftermarketProduct}
            isActionDisabled={
              !selectedVendor.length ||
              !productName ||
              (!configurations.length && !productManagedByApi && !isPartnerAccount) ||
              dateValidationError
            }
          />

          <EditRestrictionReasonMessage
            reason={editRestrictionReason}
            isSuperAdminOrSingleAccount={isSuperAdminOrSingleAccount}
          />

          <VendorSection
            canWrite={canWrite}
            selectedVendor={selectedVendor}
            isPartnerAccount={isPartnerAccount}
            availableVendors={availableVendors}
            setSelectedVendor={setSelectedVendor}
            isPartnerLinksLoading={isPartnerLinksLoading}
          />

          <RenderWithCondition condition={selectedVendor?.length > 0 || selectedVendor?.length === 0}>
            <Paper style={{ padding: '1rem' }}>
              <Stack gap="0.5rem">
                <Typography variant="subtitle1" color="primary">
                  <LooksTwoIcon /> Product Configuration
                </Typography>

                <FormInput
                  required
                  variant="outlined"
                  value={productName}
                  disabled={!canWrite}
                  onChange={v => setProductName(v.toString())}
                  label="Product Name - The applicant will be presented with this name"
                />

                {productManagedByApi && (
                  <TextField
                    fullWidth
                    id={`externalId`}
                    label="Vendor's Product ID - Used for API Integration"
                    variant="outlined"
                    size="small"
                    value={externalId || ''}
                    onChange={e => setExternalId(e.target.value)}
                    disabled
                    slotProps={{
                      inputLabel: {
                        shrink: !!externalId,
                      },
                    }}
                  />
                )}

                <LogoSection
                  canWrite={canWrite}
                  productLogoUrl={productLogoUrl}
                  setProductLogo={setProductLogo}
                  productLogoPreview={productLogoPreview}
                  setProductLogoPreview={setProductLogoPreview}
                  handleProductLogoChange={handleProductLogoChange}
                />

                <Typography style={{ paddingTop: '8px' }}>Product Type</Typography>
                <RadioGroup row value={productType} onChange={e => setProductType(e.target.value)}>
                  {convertEnumToSelectOptions(AftermarketProductCategory).map(({ value, label }) => (
                    <FormControlLabel
                      key={value}
                      value={value}
                      label={label}
                      disabled={!canWrite}
                      control={<Radio color="primary" />}
                    />
                  ))}
                </RadioGroup>

                <Typography>Product or Addon?</Typography>
                <RadioGroup
                  row
                  value={baseOrOption}
                  onChange={e => updateBaseOrOption(e.target.value as AftermarketProductType)}
                >
                  <FormControlLabel
                    value={AftermarketProductType.Product}
                    control={<Radio color="primary" />}
                    label={'Product'}
                    disabled={!canWrite}
                  />
                  <FormControlLabel
                    value={AftermarketProductType.Addon}
                    control={<Radio color="primary" />}
                    label={'Addon'}
                    disabled={!canWrite}
                  />
                </RadioGroup>

                <Typography style={{ paddingTop: '16px' }}>Product Availability (Optional)</Typography>
                <Box display="flex" gap="1rem" flexWrap="wrap">
                  <Box flex="1" minWidth="200px">
                    <LocalizationProvider dateAdapter={AdapterMoment}>
                      <DatePicker
                        label="First Date Available"
                        value={firstDateAvailable ? moment(firstDateAvailable) : null}
                        onChange={date => {
                          const newDate = date ? date.format() : null;
                          setFirstDateAvailable(newDate);
                          setDateValidationError(validateDates(newDate, lastDateAvailable));
                        }}
                        disabled={!canWrite}
                        slotProps={{
                          textField: {
                            fullWidth: true,
                            size: 'small',
                            error: dateValidationError,
                          },
                        }}
                      />
                    </LocalizationProvider>
                  </Box>
                  <Box flex="1" minWidth="200px">
                    <LocalizationProvider dateAdapter={AdapterMoment}>
                      <DatePicker
                        label="Last Date Available"
                        value={lastDateAvailable ? moment(lastDateAvailable) : null}
                        onChange={date => {
                          const newDate = date ? date.endOf('day').format() : null;
                          setLastDateAvailable(newDate);
                          setDateValidationError(validateDates(firstDateAvailable, newDate));
                        }}
                        disabled={!canWrite}
                        slotProps={{
                          textField: {
                            fullWidth: true,
                            size: 'small',
                            error: dateValidationError,
                            helperText: dateValidationError ? 'Last date must be after first date' : '',
                          },
                        }}
                      />
                    </LocalizationProvider>
                  </Box>
                </Box>
              </Stack>

              <Divider variant="fullWidth" />

              <TabsContainer<AftermarketProductConfigTabs> forceTabValue={forceTab} tabs={tabs} />
            </Paper>

            <DocumentsCard canWrite={canWrite} documents={documents} setDocuments={setDocuments} />
          </RenderWithCondition>
        </Stack>
      </RenderWithFallback>
    </PageContainer>
  );
};
